/* eslint-disable max-lines-per-function */
/* eslint-disable max-statements */
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { withMetrics } from '@catalogo/core-metrics';
import { getHandleVisible, getHandleExpand } from '../helpers/accordion-box';
import { IReceivedProps, IInjectedProps } from '../types/accordion-box';

export const withAccordionBox = <P extends IReceivedProps>(Component: React.FC<P>) => {
  const AccordionBoxComponent: React.FC<P & IInjectedProps> = props => {
    const {
      forwardedRef,
      shouldGetElementHeightOnRender,
      metricId,
      metricCategory,
      metricAction,
      dispatchMetrics,
      anchorRef,
      initialDefaultHeight = 0,
      visibilityOnInit = false,
      expandContentOption = false,
      forceVisible = false,
    } = props;

    const location = useLocation();
    const [isVisible, setIsVisible] = useState<boolean>(shouldGetElementHeightOnRender ?? visibilityOnInit);
    const [expandedContent, setExpandedContent] = useState<boolean>(
      shouldGetElementHeightOnRender ? true : !expandContentOption
    );
    const [initialHeight, setInitialHeight] = useState<number>(initialDefaultHeight);
    const [maxHeight, setMaxHeight] = useState<string | number>();

    const toggleVisible = getHandleVisible(
      isVisible,
      setIsVisible,
      dispatchMetrics,
      metricId,
      metricCategory,
      metricAction
    );

    const expandContent = getHandleExpand(setExpandedContent, dispatchMetrics);

    useEffect(() => {
      const hash = location?.hash;
      if ((hash && anchorRef && hash.includes(anchorRef)) || forceVisible) {
        setExpandedContent(true);
        setIsVisible(true);
      }
    }, [location, forceVisible]);

    useEffect(() => {
      if (shouldGetElementHeightOnRender) {
        const heightElementRef = (forwardedRef as React.MutableRefObject<HTMLElement>)?.current?.offsetHeight ?? 0;
        const heightElementInitial = expandContentOption ? initialDefaultHeight : heightElementRef;
        setInitialHeight(heightElementInitial);
        setMaxHeight(heightElementRef);
        if (initialDefaultHeight < heightElementRef) {
          setExpandedContent(!expandContentOption);
        }
      } else {
        setMaxHeight('9999vh');
      }
      setIsVisible(visibilityOnInit);
    }, []);

    let maxHeightCurrent: string | number = 0;
    if (isVisible) {
      maxHeightCurrent = !expandedContent ? initialHeight : (maxHeight as number);
    }
    const maxHeightCurrentFormatted = typeof maxHeightCurrent === 'number' ? `${maxHeightCurrent}px` : maxHeightCurrent;
    const showExpandButton = isVisible && expandContentOption && !expandedContent;

    const injectedProps = {
      forwardedRef,
      maxHeightCurrent: maxHeightCurrentFormatted,
      showExpandButton,
      toggleVisible,
      expandContent,
      isVisible,
    };

    return <Component {...props} {...injectedProps} />;
  };

  const ComponentWithMetrics = withMetrics(AccordionBoxComponent);

  const ComponentWithRef = React.forwardRef<HTMLDivElement, P & IInjectedProps>((props, ref) => (
    <ComponentWithMetrics {...props} forwardedRef={ref} />
  ));

  return ComponentWithRef;
};

export default withAccordionBox;
